import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCustomDialog } from '../../context/DialogContext';

const ManageQR = ({ token }) => {
    const { alert: customAlert } = useCustomDialog();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showPrompt, setShowPrompt] = useState(false);
    const [promptInput, setPromptInput] = useState("15");
    const [activeTemplate, setActiveTemplate] = useState(null);

    useEffect(() => {
        const fetchTemplates = async () => {
            try {
                const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_qr.php?action=fetch', {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await res.json();
                if (data.success) {
                    setTemplates(data.templates);
                }
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchTemplates();
    }, [token]);

    const handleDownload = async (url, filename) => {
        try {
            // Append timestamp to bypass aggressive browser caching of previously failed downloads
            const cacheBustUrl = url + (url.includes('?') ? '&' : '?') + 't=' + Date.now();
            
            const response = await fetch(cacheBustUrl, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) {
                const text = await response.text();
                let errorMsg = 'Download failed';
                try {
                    const json = JSON.parse(text);
                    errorMsg = json.message || errorMsg;
                } catch {
                    errorMsg = text || errorMsg;
                }
                throw new Error(errorMsg);
            }
            
            const contentType = response.headers.get('Content-Type');
            if (contentType && contentType.includes('application/json')) {
                const data = await response.json();
                throw new Error(data.message || 'Server error.');
            }

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
                window.URL.revokeObjectURL(downloadUrl);
                document.body.removeChild(a);
                localStorage.setItem('qr_downloaded', 'true');
            }, 100);
        } catch (error) {
            console.error('Error downloading QR:', error);
            await customAlert(`Failed: ${error.message}`, 'Download Error', 'danger');
        }
    };

    return (
        <div className="admin-card">
            <Link to="/manage_v2_secure_auth/" className="sticky-back-btn">
    <i className="fas fa-arrow-left"></i> Back to Dashboard
</Link>
            <h1 style={{marginTop: '0'}}>Download QR Code Templates</h1>
            <p>Choose a design below to download a print-ready image. Downloading a PDF will give you a single file containing unique QR codes for all your tables.</p>

            {loading ? <p>Loading templates...</p> : (
                <>
                    <style>{`
                        .qr-grid {
                            display: grid;
                            grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
                            gap: 30px;
                            margin-top: 20px;
                        }
                        .qr-card {
                            border: 1px solid var(--border-color);
                            border-radius: 10px;
                            background-color: var(--bg-color);
                            padding: 15px;
                            display: flex;
                            flex-direction: column;
                        }
                        .qr-btn-row {
                            display: flex;
                            gap: 10px;
                        }
                        .qr-btn {
                            flex: 1;
                            padding: 12px;
                            color: white;
                            text-align: center;
                            font-weight: 600;
                            border-radius: 50px;
                            border: none;
                            cursor: pointer;
                        }
                        @media (max-width: 600px) {
                            .qr-grid {
                                grid-template-columns: repeat(2, 1fr);
                                gap: 10px;
                            }
                            .qr-card {
                                padding: 10px;
                            }
                            .qr-btn-row {
                                gap: 5px;
                            }
                            .qr-btn {
                                padding: 8px 5px;
                                font-size: 11px;
                            }
                            .plain-qr-title {
                                font-size: 13px;
                            }
                            .plain-qr-icon {
                                font-size: 2.5rem !important;
                            }
                        }
                    `}</style>
                <div className="qr-grid">
                    {templates.length === 0 ? (
                        <p>No templates available currently.</p>
                    ) : (
                        <>
                            <div className="qr-card">
                                <div style={{width: '100%', aspectRatio: '1/1.4', backgroundColor: '#fff', borderRadius: '6px', marginBottom: '15px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '1px dashed #ccc'}}>
                                    <i className="fas fa-qrcode plain-qr-icon" style={{fontSize: '4rem', color: '#000', marginBottom: '10px'}}></i>
                                    <div className="plain-qr-title" style={{fontWeight: 'bold', color: '#333', textAlign: 'center'}}>Plain QR Code</div>
                                    <div style={{fontSize: '0.8rem', color: '#666', textAlign: 'center'}}>(Space Saving)</div>
                                </div>
                                
                                <div style={{display: 'flex', flexDirection: 'column', gap: '10px', marginTop: 'auto'}}>
                                    <div className="qr-btn-row">
                                        <button 
                                            onClick={() => handleDownload(`https://menu.searchisend.in/apimenu/generate_qr_image.php?plain=true`, `qr_plain.png`)}
                                            className="qr-btn" style={{backgroundColor: 'var(--green-accent)'}}>
                                            PNG
                                        </button>
                                        <button 
                                            onClick={() => handleDownload(`https://menu.searchisend.in/apimenu/generate_qr_pdf.php?plain=true`, `qr_plain.pdf`)}
                                            className="qr-btn" style={{backgroundColor: 'var(--text-color-secondary)'}}>
                                            PDF
                                        </button>
                                    </div>
                                    <button 
                                        onClick={() => {
                                            setActiveTemplate({ id: 'plain', template_name: 'plain' });
                                            setPromptInput("15");
                                            setShowPrompt(true);
                                        }}
                                        className="qr-btn" style={{width: '100%', backgroundColor: 'var(--red-accent)'}}>
                                        Bulk PDF
                                    </button>
                                </div>
                            </div>
                        {templates.map(tpl => (
                            <div key={tpl.id} className="qr-card">
                                <img src={tpl.image_url} alt={tpl.template_name} style={{width: '100%', height: 'auto', aspectRatio: '1/1.4', objectFit: 'cover', borderRadius: '6px', marginBottom: '15px'}} />
                                
                                <div style={{display: 'flex', flexDirection: 'column', gap: '10px', marginTop: 'auto'}}>
                                    <div className="qr-btn-row">
                                        <button 
                                            onClick={() => handleDownload(`https://menu.searchisend.in/apimenu/generate_qr_image.php?template_id=${tpl.id}`, `qr_${tpl.template_name}.png`)}
                                            className="qr-btn" style={{backgroundColor: 'var(--green-accent)'}}>
                                            PNG
                                        </button>
                                        <button 
                                            onClick={() => handleDownload(`https://menu.searchisend.in/apimenu/generate_qr_pdf.php?template_id=${tpl.id}`, `qr_${tpl.template_name}.pdf`)}
                                            className="qr-btn" style={{backgroundColor: 'var(--text-color-secondary)'}}>
                                            PDF
                                        </button>
                                    </div>
                                    <button 
                                        onClick={() => {
                                            setActiveTemplate(tpl);
                                            setPromptInput("15");
                                            setShowPrompt(true);
                                        }}
                                        className="qr-btn" style={{width: '100%', backgroundColor: 'var(--red-accent)'}}>
                                        Bulk PDF
                                    </button>
                                </div>
                            </div>
                        ))}
                        </>
                    )}
                </div>
                </>
            )}

            {showPrompt && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <div style={{ background: 'var(--bg-color)', padding: '24px', borderRadius: '12px', width: '90%', maxWidth: '350px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
                        <h3 style={{ marginTop: 0, marginBottom: '15px', color: 'var(--text-color)' }}>Confirm Tables</h3>
                        <p style={{ margin: '0 0 15px 0', fontSize: '0.9rem', color: 'var(--text-color-secondary)' }}>Please enter your total number of tables:</p>
                        <input 
                            type="number" 
                            min="1"
                            value={promptInput}
                            onChange={(e) => setPromptInput(e.target.value)}
                            style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border-color)', marginBottom: '20px', fontSize: '1rem', boxSizing: 'border-box', backgroundColor: 'var(--card-bg)', color: 'var(--text-color)' }}
                            autoFocus
                        />
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <button onClick={() => setShowPrompt(false)} style={{ padding: '8px 16px', border: '1px solid var(--border-color)', background: 'transparent', borderRadius: '50px', cursor: 'pointer', color: 'var(--text-color)' }}>Cancel</button>
                            <button onClick={() => {
                                const count = parseInt(promptInput, 10);
                                if (!isNaN(count) && count > 0) {
                                    const url = activeTemplate.id === 'plain' 
                                        ? `https://menu.searchisend.in/apimenu/generate_qr_bulk_pdf.php?plain=true&tables=${count}`
                                        : `https://menu.searchisend.in/apimenu/generate_qr_bulk_pdf.php?template_id=${activeTemplate.id}&tables=${count}`;
                                    handleDownload(url, `qr_${activeTemplate.template_name}_bulk.pdf`);
                                    setShowPrompt(false);
                                } else {
                                    customAlert("Please enter a valid number of tables.", "Invalid Input", "danger");
                                }
                            }} style={{ padding: '8px 16px', border: 'none', background: 'var(--primary-orange)', color: '#fff', borderRadius: '50px', cursor: 'pointer', fontWeight: 'bold' }}>Download</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageQR;
